import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import WorldLabel from './WorldLabel';
import * as THREE from 'three';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { proxied } from '../services/assets';
import { IS_MOBILE, VIDEO_OFF } from '../config';
import { ROOMS } from '../rooms';
import { listWorldObjects } from '../services/worldObjects';
import CameraBasisProbe from './CameraBasisProbe';
import SplatWorld from './SplatWorld';

const ACCENT = '#9dff7d';

// Live placement editor for the featured sleeves in the splat. ?cards=edit still
// deep-links, but the ⚙ hub is the way in — see AdminPanel / RecordCardsPanel.
// Read as a hook at each use site so toggling the tool takes effect live.
const CARDS_PARAM = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('cards') === 'edit';
const useCardsEdit = () => useWorldStore((s) => s.adminTool) === 'cards' || CARDS_PARAM;

// The shelf's SHIPPED arrangement lives in rooms.js with the room's other
// placeables; saved rows in world_objects override it. ProceduralShop (the
// pre-splat fallback) still reads it directly for its own layout.

// One album face — a still sleeve or, when the track has an animated artwork
// loop and `allowVideo`, a muted VideoTexture (the record-store showcase for
// the animated-artwork rule). Clicking plays the track through the audio engine
// (single-source), and calls onPlay so the turntable can pick it up.
function AlbumArt({ track, size = 1, position = [0, 0, 0], rotation = [0, 0, 0], allowVideo = true, onPlay, onHover }) {
  const [tex, setTex] = useState(null);
  const [hover, setHover] = useState(false);
  const meshRef = useRef();
  // Hover is lifted so the SLEEVE can carry a heart without the heart owning the
  // hit area — a button floating over a record you are trying to click is how
  // you end up unable to play anything.
  useEffect(() => { onHover?.(hover); }, [hover]);   // eslint-disable-line react-hooks/exhaustive-deps
  const anim = allowVideo ? (track.artwork_animation_url || track.artwork_animated_url) : null;

  useEffect(() => {
    if (anim && !VIDEO_OFF) {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = proxied(anim);
      video.loop = true; video.muted = true; video.playsInline = true; video.preload = 'auto';
      video.play().catch(() => {});
      const t = new THREE.VideoTexture(video);
      t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
      setTex(t);
      return () => { try { video.pause(); video.removeAttribute('src'); video.load(); } catch { /* noop */ } t.dispose(); };
    }
    if (!track.artwork_url) return undefined;
    const t = new THREE.TextureLoader().load(
      proxied(track.artwork_url),
      (img) => { img.colorSpace = THREE.SRGBColorSpace; img.anisotropy = 8; img.needsUpdate = true; },
      undefined,
      () => console.warn('[record-store] art failed:', track.title),
    );
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    setTex(t);
    return () => t.dispose();
  }, [anim, track.artwork_url]);

  useFrame(() => {
    if (meshRef.current) {
      const target = hover ? 1.06 : 1;
      meshRef.current.scale.x += (target - meshRef.current.scale.x) * 0.2;
      meshRef.current.scale.y = meshRef.current.scale.x;
    }
  });

  if (!tex) return null;
  const onClick = (e) => { e.stopPropagation(); audioEngine.playTrack(track); onPlay?.(track); };

  return (
    <group position={position} rotation={rotation}>
      {/* sleeve backing / glow frame */}
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[size + 0.08, size + 0.08]} />
        <meshBasicMaterial color={hover ? ACCENT : '#0b0b0e'} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh
        ref={meshRef}
        onClick={onClick}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// The listening turntable — a console with a platter that carries the current
// pick's sleeve and spins while something is playing.
function Turntable({ nowPlaying, position = [0, 0, 0] }) {
  const platter = useRef();
  const [tex, setTex] = useState(null);
  useFrame((_, dt) => { if (platter.current && nowPlaying) platter.current.rotation.y += dt * 3.4; });

  useEffect(() => {
    setTex((old) => { old?.dispose?.(); return null; });
    if (!nowPlaying?.artwork_url) return undefined;
    const t = new THREE.TextureLoader().load(proxied(nowPlaying.artwork_url), (img) => { img.colorSpace = THREE.SRGBColorSpace; img.needsUpdate = true; });
    t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => t.dispose();
  }, [nowPlaying?.track_id, nowPlaying?.artwork_url]);

  return (
    <group position={position}>
      {/* cabinet */}
      <mesh castShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[1.5, 1.0, 1.1]} />
        <meshStandardMaterial color="#1b1620" roughness={0.6} metalness={0.35} />
      </mesh>
      {/* deck top */}
      <mesh position={[0, 1.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[1.4, 1.0]} />
        <meshStandardMaterial color="#0d0d12" roughness={0.5} metalness={0.4} />
      </mesh>
      {/* platter + record — lies flat on the deck (cylinder axis is vertical by
          default); the group spins it on Y. */}
      <group ref={platter} position={[-0.18, 1.03, 0]}>
        <mesh>
          <cylinderGeometry args={[0.42, 0.42, 0.03, 40]} />
          <meshStandardMaterial color="#08080b" roughness={0.35} metalness={0.5} emissive={ACCENT} emissiveIntensity={nowPlaying ? 0.12 : 0} />
        </mesh>
        {tex && (
          <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[0.2, 32]} />
            <meshBasicMaterial map={tex} toneMapped={false} />
          </mesh>
        )}
      </group>
      {/* tonearm */}
      <mesh position={[0.5, 1.05, -0.32]} rotation={[0, nowPlaying ? -0.5 : -0.2, 0]}>
        <boxGeometry args={[0.05, 0.04, 0.6]} />
        <meshStandardMaterial color="#c9c9d2" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

// One sleeve, positioned from world_objects. No keyboard, no localStorage: the
// record store is a first-person room whose camera already listens for arrows
// and WASD on window, so a placement handler bound to those keys moved the
// camera and the sleeve together. RecordCardsPanel owns the editing now.
function PlacedCard({ track, obj, selected, onPlay, liked, onLike }) {
  const [hover, setHover] = useState(false);
  const [onBtn, setOnBtn] = useState(false);

  // THE BUTTON IS ALWAYS MOUNTED. It used to appear only while the sleeve was
  // hovered, which made it impossible to press: the pointer has to LEAVE the
  // record to reach a control sitting under the record, and leaving unmounted
  // the control before the pointer arrived. Khari has now hit this exact shape
  // twice — a hover-gated button placed outside its own hover target is not a
  // discoverability trade-off, it is a button that does not work.
  //
  // So visibility is not the lever; PROMINENCE is. Resting it is a bare heart,
  // small enough to read as part of the sleeve. Looking at the record (or at the
  // button) spells it out. Either way it is there, and it is pressable.
  const warm = hover || onBtn || liked;
  return (
    <group>
      {/* Animated artwork wins wherever a track has one — that is the house rule,
          and a still where a loop exists always reads as the lesser version.
          The only brake is the phone's video budget: several decoders on one
          wall is what that limit exists for, so mobile falls back to the still. */}
      <AlbumArt
        track={track}
        size={obj.size}
        position={obj.pos}
        rotation={[0, obj.rotY, 0]}
        allowVideo={!IS_MOBILE}
        onPlay={onPlay}
        onHover={setHover}
      />
      {/* Clear of the sleeve's frame (which is size + 0.08), so the two never
          share a pixel and the record keeps its whole face as the play target. */}
      <WorldLabel
        position={[obj.pos[0], obj.pos[1] - obj.size * 0.74, obj.pos[2]]}
        center
        distanceFactor={5}
        style={{ pointerEvents: 'auto' }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onLike?.(track); }}
          onPointerEnter={() => setOnBtn(true)}
          onPointerLeave={() => setOnBtn(false)}
          title={liked ? 'In your likes — tap to remove' : 'Save to your likes'}
          style={{
            cursor: 'pointer', border: `1px solid ${liked ? 'rgba(255,94,120,0.75)' : `rgba(255,255,255,${warm ? 0.32 : 0.16})`}`,
            background: liked ? 'rgba(255,94,120,0.22)' : `rgba(10,10,12,${warm ? 0.74 : 0.42})`,
            color: liked ? '#ff5e78' : '#f2f2f4', borderRadius: 999,
            padding: warm ? '4px 10px' : '4px 8px', font: '11px system-ui', lineHeight: 1.5,
            opacity: warm ? 1 : 0.72,
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', whiteSpace: 'nowrap',
            transition: 'opacity .14s ease, background .14s ease, border-color .14s ease',
          }}
        >
          {liked ? (warm ? '♥ saved' : '♥') : (warm ? '♡ save' : '♡')}
        </button>
      </WorldLabel>
      {selected && (
        <WorldLabel position={[obj.pos[0], obj.pos[1] + obj.size * 0.75, obj.pos[2]]} center distanceFactor={6} style={{ pointerEvents: 'none' }}>
          <div style={{ background: '#ffb25e', color: '#1c1206', font: '600 11px system-ui', padding: '3px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
            slot {(obj.slot ?? 0) + 1} ◄ placing
          </div>
        </WorldLabel>
      )}
    </group>
  );
}

// Interactive layer inside the photoreal splat: clickable featured releases +
// a now-playing readout. The splat supplies the shop; this is what you touch.
//
// THE SHELF IS THE OBJECT LIST. Tracks are sliced to however many sleeve
// positions exist, so a shop with three placed slots shows three records however
// many the API returns — which is why the wall emptied when the splat loaded and
// the eight-sleeve procedural fallback stopped standing in.
function SplatOverlay({ tracks, nowPlaying, onPlay, liked, onLike }) {
  const EDIT = useCardsEdit();
  const objects = useWorldStore((s) => s.worldObjects);
  const setObjects = useWorldStore((s) => s.loadWorldObjectSlice);
  const sel = useWorldStore((s) => s.worldObjectSel);

  // Saved arrangement wins; the room's authored `cards` are the fallback so the
  // shop ships hung rather than bare before anyone has arranged it live.
  useEffect(() => {
    let dead = false;
    listWorldObjects('record-store', 'card').then((rows) => {
      if (dead) return;
      if (rows.length) { setObjects('record-store', 'card', rows); return; }
      setObjects('record-store', 'card', (ROOMS['record-store'].cards || []).map((c, i) => ({
        object_id: 'authored:' + i, zone_id: 'record-store', kind: 'card',
        ref: null, slot: i, pos: c.pos, rotY: c.rotY || 0, size: c.size || 0.5,
      })));
    });
    return () => { dead = true; };
  }, [setObjects]);

  const slots = useMemo(
    () => objects.filter((o) => o.zone_id === 'record-store' && o.kind === 'card')
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)),
    [objects],
  );
  const featured = useMemo(() => tracks.slice(0, slots.length), [tracks, slots.length]);

  // Say so when the shelf is smaller than the catalogue. A silent slice reads as
  // "there are only three records" rather than "there are only three places to
  // put one", and that cost a debugging session.
  useEffect(() => {
    if (slots.length && tracks.length > slots.length) {
      console.log('[cards] ' + tracks.length + ' featured tracks, ' + slots.length
        + ' placed slots — showing ' + slots.length + '. Add slots in ⚙ → Record sleeves.');
    }
  }, [tracks.length, slots.length]);

  // Caption + key light follow the sleeves' CENTROID so they never strand at the
  // old origin when the shelf is re-placed.
  const center = useMemo(() => {
    if (!slots.length) return [0, 1.6, -0.6];
    const s = slots.reduce((a, c) => [a[0] + c.pos[0], a[1] + c.pos[1], a[2] + c.pos[2]], [0, 0, 0]);
    return [s[0] / slots.length, s[1] / slots.length, s[2] / slots.length];
  }, [slots]);

  return (
    <group>
      {EDIT && <CameraBasisProbe />}
      {featured.map((t, i) => (
        <PlacedCard
          key={t.track_id}
          track={t}
          obj={slots[i]}
          selected={EDIT && sel === slots[i].object_id}
          onPlay={onPlay}
          liked={liked?.has(Number(t.track_id))}
          onLike={onLike}
        />
      ))}
      <Text position={[center[0], center[1] - 0.36, center[2]]} rotation={[0, -0.65, 0]} fontSize={0.16} color="#c8c8d0" anchorX="center" anchorY="middle">
        {nowPlaying ? '♪ ' + nowPlaying.title : 'tap to play'}
      </Text>
      <pointLight position={[center[0], center[1] + 0.5, center[2]]} color={ACCENT} intensity={3} distance={6} />
    </group>
  );
}

function ProceduralShop({ tracks, nowPlaying, onPlay }) {
  const wall = useMemo(() => tracks.slice(0, 8), [tracks]);
  const crate = useMemo(() => {
    const rest = tracks.length > 8 ? tracks.slice(8) : tracks;
    return rest.slice(0, 7);
  }, [tracks]);

  return (
    <group>
      {/* ── Shell ─────────────────────────────────────────────────────── */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[11, 10]} />
        <meshStandardMaterial color="#20161a" roughness={1} />
      </mesh>
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <planeGeometry args={[2.4, 9]} />
        <meshStandardMaterial color="#2c1f18" roughness={1} />
      </mesh>
      {[
        { p: [0, 2.3, -4.4], r: [0, 0, 0], s: [11, 4.6] },
        { p: [-5.5, 2.3, 0], r: [0, Math.PI / 2, 0], s: [10, 4.6] },
        { p: [5.5, 2.3, 0], r: [0, -Math.PI / 2, 0], s: [10, 4.6] },
        { p: [0, 2.3, 5], r: [0, Math.PI, 0], s: [11, 4.6] },
      ].map((w, i) => (
        <mesh key={i} position={w.p} rotation={w.r}>
          <planeGeometry args={w.s} />
          <meshStandardMaterial color="#2a1d24" roughness={1} />
        </mesh>
      ))}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 4.6, 0]}>
        <planeGeometry args={[11, 10]} />
        <meshStandardMaterial color="#140f13" roughness={1} />
      </mesh>

      {/* ── Neon sign ─────────────────────────────────────────────────── */}
      <mesh position={[0, 4.0, -4.35]}>
        <planeGeometry args={[6.2, 0.9]} />
        <meshBasicMaterial color="#0a0a0c" toneMapped={false} />
      </mesh>
      <Text position={[0, 4.0, -4.3]} fontSize={0.6} letterSpacing={0.08} color={ACCENT} anchorX="center" anchorY="middle" outlineWidth={0.01} outlineColor={ACCENT}>
        WAX &amp; VINYL
      </Text>

      {/* ── Back wall: animated featured releases ─────────────────────── */}
      {wall.map((t, i) => {
        const col = i % 4, row = Math.floor(i / 4);
        const x = -3.3 + col * 2.2;
        const y = 2.9 - row * 1.35;
        return <AlbumArt key={t.track_id} track={t} size={1.05} position={[x, y, -4.25]} onPlay={onPlay} />;
      })}

      {/* ── Front crate: flip-through sleeves ─────────────────────────── */}
      <group position={[-1.7, 0, 1.4]}>
        <mesh castShadow position={[0, 0.55, 0]}>
          <boxGeometry args={[2.4, 1.1, 1.0]} />
          <meshStandardMaterial color="#37271d" roughness={0.95} />
        </mesh>
        {crate.map((t, i) => (
          <AlbumArt
            key={t.track_id}
            track={t}
            size={0.86}
            // Was hard-off. Same rule as the wall: prefer the loop, and let the
            // phone's video budget — not a blanket no — be the thing that says no.
            allowVideo={!IS_MOBILE}
            position={[-0.9 + i * 0.28, 1.06, -0.05 + i * 0.02]}
            rotation={[-0.62, 0, 0]}
            onPlay={onPlay}
          />
        ))}
      </group>

      {/* ── Listening station ─────────────────────────────────────────── */}
      <Turntable nowPlaying={nowPlaying} position={[2.7, 0, -1.4]} />
      <Text position={[2.7, 1.85, -1.4]} fontSize={0.16} color="#9a9aa6" anchorX="center">
        {nowPlaying ? `♪ ${nowPlaying.title}` : 'pick a record'}
      </Text>

      {/* ── Light ─────────────────────────────────────────────────────── */}
      <ambientLight intensity={0.25} color="#40303a" />
      <pointLight position={[0, 4.2, -2]} color={ACCENT} intensity={9} distance={12} />
      <pointLight position={[-1.7, 3.2, 1.4]} color="#ffb25e" intensity={7} distance={9} castShadow />
      <pointLight position={[2.7, 2.6, -1.4]} color="#ff9a3c" intensity={5} distance={7} />
    </group>
  );
}

// The Record Store — "Wax & Vinyl". A blend of a photoreal Marble splat (the
// shop itself) and live 3D (the clickable featured records + turntable), the
// same recipe as the Listening Room. Everything is click-to-play via the
// single-source audio engine.
export default function RecordStore() {
  const [tracks, setTracks] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [splatReady, setSplatReady] = useState(false);
  const [liked, setLiked] = useState(() => new Set());
  const room = ROOMS['record-store'];

  // WHAT IS ON THE WALL. The featured records are the shop's own display; the
  // clerk's picks replace them while you are being served, and putting the
  // display back is what closing the counter means (worldStore.closeShop).
  //
  // `??` and not `||` — an empty array from him is a wall he cleared, and only
  // null means "nobody has recommended anything".
  const shelf = useWorldStore((s) => s.shelfTracks);
  const wall = shelf ?? tracks;

  useEffect(() => {
    api.featuredTracks().then((d) => setTracks(d?.tracks || [])).catch(() => setTracks([]));
  }, []);

  // Which of these are already saved, so a heart starts filled rather than
  // lying. Guests get an empty set and a heart that asks them to sign in.
  useEffect(() => {
    let dead = false;
    import('../services/recordShop').then(({ likedIds }) => likedIds())
      .then((s) => { if (!dead) setLiked(s); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  // Optimistic, because a heart that waits on a round trip reads as broken —
  // and rolled back out loud if the save actually failed.
  const onLike = async (track) => {
    const id = Number(track.track_id);
    const { likeTrack, unlikeTrack } = await import('../services/recordShop');
    const was = liked.has(id);
    setLiked((cur) => { const n = new Set(cur); if (was) n.delete(id); else n.add(id); return n; });
    try {
      if (was) await unlikeTrack(id); else await likeTrack(id);
    } catch (err) {
      setLiked((cur) => { const n = new Set(cur); if (was) n.add(id); else n.delete(id); return n; });
      console.warn('[record-store] like failed:', err.message);
    }
  };

  return (
    <group>
      {/* Photoreal Marble splat interior (when a manifest exists). */}
      {room.manifest && (
        <SplatWorld
          manifestUrl={room.manifest}
          // Don't push the manifest spawn — rooms.js spawn is authoritative here
          // so ?cam=edit "copy spawn" can re-aim the entry framing.
          onLoaded={() => setSplatReady(true)}
          onError={() => setSplatReady(false)}
        />
      )}

      {/* Blend: splat = room, overlay = the interactive parts.
          THE PROCEDURAL SHOP IS FOR A ROOM WITH NO SPLAT AT ALL — not for a
          splat that has not finished loading (Khari, 2026-08-04: "it shows these
          other options which are not actually in the room, like the record
          player and a row of records to the left… Both of these objects are
          wasted renders"). It is a different shop: its own walls, its own crate,
          its own turntable, none of which exist in Wax & Vinyl. Standing it in
          for a beat draws furniture nobody can reach and teaches the room wrong.
          A room WITH a manifest shows its sleeves and a light while the splat
          streams — the records are the part you can actually use. */}
      {room.manifest || splatReady
        ? (
          <>
            <SplatOverlay tracks={wall} nowPlaying={nowPlaying} onPlay={setNowPlaying} liked={liked} onLike={onLike} />
            {!splatReady && <ambientLight intensity={0.55} />}
          </>
        )
        : <ProceduralShop tracks={wall} nowPlaying={nowPlaying} onPlay={setNowPlaying} />}
    </group>
  );
}
